/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import {
  createEmptyBlackoutDayOfWeekRuleGroup,
  EditableBlackoutDayOfWeekRuleGroup,
} from "./blackoutDayOfWeek";
import { getResource } from "../../../language/resources/getResource";
import { useCallback, useMemo, useState } from "react";
import { BlackoutDayOfWeekRuleRow } from "./BlackoutDayOfWeekRuleRow";
import { TimeFormat } from "../../../forms/elements/timeInputUtils";
import * as Buttons from "../../../forms/elements/buttons";
import { accessRuleFieldset } from "../ruleEditorStyles";

type Props = {
  resources: Record<string, any>;
  timeFormat: TimeFormat;
  ruleGroups: EditableBlackoutDayOfWeekRuleGroup[];
  onChange: (groups: EditableBlackoutDayOfWeekRuleGroup[]) => void;
  submitCount: number;
  hasChangesSinceSubmit: boolean;
  onAnyEdit: () => void;
  rootCss?: SerializedStyles;
};

export function BlackoutDayOfWeekAccessRuleEditor({
  resources,
  timeFormat,
  ruleGroups,
  onChange,
  submitCount,
  hasChangesSinceSubmit,
  onAnyEdit,
  rootCss,
}: Props) {
  const [localRuleGroups, setLocalRuleGroups] = useState<
    EditableBlackoutDayOfWeekRuleGroup[]
  >(() => ruleGroups);

  const dayDefinitions = useMemo(
    () => [
      { id: 0, label: getResource(resources, "SHORT_DAY_OF_WEEK_SUN") },
      { id: 1, label: getResource(resources, "SHORT_DAY_OF_WEEK_MON") },
      { id: 2, label: getResource(resources, "SHORT_DAY_OF_WEEK_TUE") },
      { id: 3, label: getResource(resources, "SHORT_DAY_OF_WEEK_WED") },
      { id: 4, label: getResource(resources, "SHORT_DAY_OF_WEEK_THU") },
      { id: 5, label: getResource(resources, "SHORT_DAY_OF_WEEK_FRI") },
      { id: 6, label: getResource(resources, "SHORT_DAY_OF_WEEK_SAT") },
    ],
    [resources],
  );

  const updateGroups = useCallback(
    (
      updater: (
        groups: EditableBlackoutDayOfWeekRuleGroup[],
      ) => EditableBlackoutDayOfWeekRuleGroup[],
    ) => {
      const updatedWithDeletions = updater(localRuleGroups);

      // Local editor: hide deleted groups
      setLocalRuleGroups(updatedWithDeletions.filter((g) => !g.isDeleted));

      // Parent: update with full group list including deleted groups
      onChange(updatedWithDeletions);
    },
    [localRuleGroups, onChange],
  );

  const addNewRuleGroup = useCallback(() => {
    onAnyEdit();
    updateGroups((groups) => [
      ...groups,
      createEmptyBlackoutDayOfWeekRuleGroup(),
    ]);
  }, [onAnyEdit, updateGroups]);

  const handleRuleGroupChanges = useCallback(
    (updatedGroup?: EditableBlackoutDayOfWeekRuleGroup) => {
      if (!updatedGroup) {
        updateGroups(() => []);
        return;
      }

      updateGroups((groups) =>
        groups.map((g) => (g.id === updatedGroup.id ? updatedGroup : g)),
      );
    },
    [updateGroups],
  );

  const handleDeleteGroup = useCallback(
    (deletedGroup: EditableBlackoutDayOfWeekRuleGroup) => {
      updateGroups((groups) =>
        groups.map((group) =>
          group.id !== deletedGroup.id
            ? group
            : {
                ...group,
                isDeleted: true,
                rules: group.rules.map((rule) => ({
                  ...rule,
                  isSelected: false,
                })),
              },
        ),
      );
    },
    [updateGroups],
  );

  return (
    <div css={rootCss}>
      <fieldset css={accessRuleFieldset}>
        <legend>
          {getResource(resources, "ACCESS_RULE_EDIT_BLACKOUT_DOW")}
        </legend>
        {localRuleGroups.map((group) => (
          <BlackoutDayOfWeekRuleRow
            key={group.id}
            resources={resources}
            timeFormat={timeFormat}
            days={dayDefinitions}
            ruleGroup={group}
            onChange={handleRuleGroupChanges}
            onDelete={handleDeleteGroup}
            submitCount={submitCount}
            hasChangesSinceSubmit={hasChangesSinceSubmit}
          />
        ))}
      </fieldset>

      <Buttons.SmallSecondary
        css={css`
          margin-top: 10px;
        `}
        type="button"
        onClick={addNewRuleGroup}
        title={getResource(resources, "ACCESS_RULE_EDIT_ADD_NEW")}
      >
        {getResource(resources, "ACCESS_RULE_EDIT_ADD_NEW")}
      </Buttons.SmallSecondary>
    </div>
  );
}
